import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AdminPanelSettings,
  AutoAwesome,
  CheckCircle,
  Close,
  FilterList,
  HourglassEmpty,
  Lock,
  Refresh,
  Reply,
  Schedule,
  Search,
  Send,
  SupportAgent,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  getAllTickets,
  getTicketById,
  addMessage,
  setTicketStatus,
  getAISuggestedReply,
} from '../utils/ticketManager';

const STATUS_OPTIONS = ['All', 'Open', 'In Progress', 'Resolved', 'Closed'];
const PRIORITY_OPTIONS = ['All', 'Low', 'Medium', 'High', 'Urgent'];

const STATUS_CONFIG = {
  Open: { color: 'info', icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
  'In Progress': { color: 'warning', icon: <Schedule sx={{ fontSize: 14 }} /> },
  Resolved: { color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  Closed: { color: 'default', icon: <Lock sx={{ fontSize: 14 }} /> },
};

const PRIORITY_CONFIG = {
  Low: { color: 'default' },
  Medium: { color: 'info' },
  High: { color: 'warning' },
  Urgent: { color: 'error' },
};

function TicketDetailDialog({ ticket, onClose, onUpdated }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [currentTicket, setCurrentTicket] = useState(ticket);

  const handleSend = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    setActionError(null);
    try {
      const updated = await addMessage(currentTicket.id, replyText.trim(), true);
      setCurrentTicket(updated);
      onUpdated(updated);
      setReplyText('');
    } catch (err) {
      setActionError('Failed to send reply. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleAISuggest = async () => {
    setAiLoading(true);
    setActionError(null);
    try {
      const suggestion = await getAISuggestedReply(currentTicket.id);
      setReplyText(suggestion);
    } catch (err) {
      setActionError('Failed to generate an AI suggestion. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    setActionError(null);
    try {
      const updated = await setTicketStatus(currentTicket.id, status);
      setCurrentTicket(updated);
      onUpdated(updated);
    } catch (err) {
      setActionError('Failed to update status. Please try again.');
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend();
  };

  const statusCfg = STATUS_CONFIG[currentTicket.status] || STATUS_CONFIG.Open;

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box flex={1}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography fontWeight={700} fontSize={16}>
                {currentTicket.subject}
              </Typography>
              <Chip
                label={currentTicket.ticketNumber}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, fontWeight: 600 }}
              />
            </Stack>
            <Stack direction="row" gap={1} mt={0.75} flexWrap="wrap">
              <Chip
                icon={statusCfg.icon}
                label={currentTicket.status}
                size="small"
                color={statusCfg.color}
              />
              <Chip label={currentTicket.category} size="small" variant="outlined" />
              <Chip
                label={currentTicket.priority}
                size="small"
                color={PRIORITY_CONFIG[currentTicket.priority]?.color || 'default'}
                variant="outlined"
              />
              <Chip
                label={currentTicket.userEmail || currentTicket.userName}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11 }}
              />
            </Stack>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      {/* Status controls */}
      <Box sx={{ px: 2.5, py: 1.25, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography fontSize={13} color="text.secondary" fontWeight={500}>
            Set status:
          </Typography>
          {['Open', 'In Progress', 'Resolved', 'Closed'].map((s) => (
            <Button
              key={s}
              size="small"
              variant={currentTicket.status === s ? 'contained' : 'outlined'}
              onClick={() => handleStatusChange(s)}
              sx={{
                fontSize: 12,
                py: 0.25,
                minWidth: 0,
                ...(currentTicket.status === s && {
                  bgcolor: '#0BA480',
                  '&:hover': { bgcolor: '#059669' },
                }),
              }}
            >
              {s}
            </Button>
          ))}
          <Typography fontSize={12} color="text.disabled" ml="auto">
            Opened {format(new Date(currentTicket.createdAt), 'MMM d, yyyy HH:mm')}
          </Typography>
        </Stack>
      </Box>

      <Divider />

      {/* Conversation */}
      <DialogContent
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          maxHeight: 380,
          overflowY: 'auto',
        }}
      >
        {currentTicket.messages.map((msg) => {
          const isAdmin = msg.sender === 'admin';
          return (
            <Box
              key={msg.id}
              sx={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}
            >
              <Box sx={{ maxWidth: '75%' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.4, textAlign: isAdmin ? 'right' : 'left' }}
                >
                  {msg.senderName} · {format(new Date(msg.timestamp), 'MMM d, HH:mm')}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    px: 1.75,
                    py: 1.25,
                    borderRadius: isAdmin ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    bgcolor: isAdmin
                      ? '#0BA480'
                      : isDark
                      ? 'rgba(255,255,255,0.06)'
                      : '#F1F5F9',
                    color: isAdmin ? '#fff' : 'text.primary',
                    border: isAdmin
                      ? 'none'
                      : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
                  }}
                >
                  <Typography fontSize={14} lineHeight={1.6} sx={{ whiteSpace: 'pre-wrap' }}>
                    {msg.message}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          );
        })}
      </DialogContent>

      {actionError && (
        <Alert severity="error" sx={{ mx: 2, mt: 1.5, borderRadius: '8px' }}>
          {actionError}
        </Alert>
      )}

      {currentTicket.status !== 'Closed' && (
        <>
          <Divider />
          <DialogActions sx={{ p: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
            <Stack direction="row" justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                startIcon={aiLoading ? <CircularProgress size={14} /> : <AutoAwesome sx={{ fontSize: 16 }} />}
                onClick={handleAISuggest}
                disabled={aiLoading}
                sx={{ color: '#0BA480', borderColor: '#0BA480', '&:hover': { borderColor: '#059669' } }}
              >
                {aiLoading ? 'Generating...' : 'AI Suggest Reply'}
              </Button>
            </Stack>
            <Stack direction="row" gap={1} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                placeholder="Type your admin reply... (Ctrl+Enter to send)"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={handleKey}
                size="small"
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                endIcon={<Send />}
                onClick={handleSend}
                disabled={!replyText.trim() || sending}
                sx={{ bgcolor: '#0BA480', '&:hover': { bgcolor: '#059669' }, flexShrink: 0 }}
              >
                Reply
              </Button>
            </Stack>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

const AdminSupport = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllTickets();
      setTickets(data);
    } catch (err) {
      setError('Failed to load tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleOpenTicket = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const fresh = await getTicketById(ticket.id, true);
      setSelectedTicket(fresh);
    } catch (err) {
      // Keep showing the cached ticket if the refetch fails
    }
  };

  const handleTicketUpdated = (updated) => {
    setSelectedTicket(updated);
    refresh();
  };

  const stats = useMemo(() => ({
    total: tickets.length,
    open: tickets.filter((t) => t.status === 'Open').length,
    inProgress: tickets.filter((t) => t.status === 'In Progress').length,
    resolved: tickets.filter((t) => t.status === 'Resolved').length,
    closed: tickets.filter((t) => t.status === 'Closed').length,
  }), [tickets]);

  const filtered = useMemo(() => {
    return tickets.filter((t) => {
      const matchStatus = statusFilter === 'All' || t.status === statusFilter;
      const matchPriority = priorityFilter === 'All' || t.priority === priorityFilter;
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        t.subject?.toLowerCase().includes(q) ||
        t.ticketNumber?.toLowerCase().includes(q) ||
        t.userName?.toLowerCase().includes(q) ||
        t.userEmail?.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q);
      return matchStatus && matchPriority && matchSearch;
    });
  }, [tickets, statusFilter, priorityFilter, search]);

  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const borderClr = isDark ? 'rgba(255,255,255,0.09)' : '#E2E8F0';

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <AdminPanelSettings sx={{ color: '#0BA480', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>
              Support Management
            </Typography>
            <Chip label="Admin" size="small" color="success" sx={{ fontWeight: 600 }} />
          </Stack>
          <Typography color="text.secondary" fontSize={14}>
            Manage customer support tickets, reply to users, and track resolutions.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={refresh}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: stats.total, color: 'text.primary' },
          { label: 'Open', value: stats.open, color: '#3B82F6' },
          { label: 'In Progress', value: stats.inProgress, color: '#F59E0B' },
          { label: 'Resolved', value: stats.resolved, color: '#0BA480' },
          { label: 'Closed', value: stats.closed, color: 'text.secondary' },
        ].map((s) => (
          <Grid item xs={6} sm={4} md={2.4} key={s.label}>
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${borderClr}`,
                bgcolor: cardBg,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#0BA480' },
              }}
              onClick={() => setStatusFilter(s.label === 'Total' ? 'All' : s.label)}
            >
              <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {s.label}
                </Typography>
                <Typography variant="h5" fontWeight={700} color={s.color}>
                  {s.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', mb: 2 }}>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack direction={isMobile ? 'column' : 'row'} gap={1.5} alignItems={isMobile ? 'stretch' : 'center'}>
            <TextField
              size="small"
              placeholder="Search tickets, users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ fontSize: 18, mr: 0.75, color: 'text.disabled' }} /> }}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              sx={{ minWidth: 140 }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Priority"
              value={priorityFilter}
              onChange={(e) => { setPriorityFilter(e.target.value); setPage(0); }}
              sx={{ minWidth: 140 }}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>
      </Card>

      {/* Table */}
      <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Ticket #</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Messages</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Loading tickets...</Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <SupportAgent sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No tickets found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((ticket) => {
                    const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.Open;
                    const hasAdminReply = ticket.messages.some((m) => m.sender === 'admin');
                    return (
                      <TableRow
                        key={ticket.id}
                        sx={{ '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'action.hover' }, cursor: 'pointer' }}
                        onClick={() => handleOpenTicket(ticket)}
                      >
                        <TableCell>
                          <Typography fontFamily="monospace" fontSize={13} fontWeight={600} color="primary.main">
                            {ticket.ticketNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13} fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                            {ticket.subject}
                          </Typography>
                          {!hasAdminReply && ticket.status === 'Open' && (
                            <Chip
                              label="Needs reply"
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ fontSize: 10, height: 18, mt: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13}>{ticket.userName}</Typography>
                          <Typography fontSize={11} color="text.secondary">{ticket.userEmail}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13}>{ticket.category}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ticket.priority}
                            size="small"
                            color={PRIORITY_CONFIG[ticket.priority]?.color || 'default'}
                            variant="outlined"
                            sx={{ fontSize: 12 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={statusCfg.icon}
                            label={ticket.status}
                            size="small"
                            color={statusCfg.color}
                            sx={{ fontSize: 12 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13}>
                            {format(new Date(ticket.createdAt), 'MMM d, yyyy')}
                          </Typography>
                          <Typography fontSize={11} color="text.secondary">
                            {format(new Date(ticket.createdAt), 'HH:mm')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13} textAlign="center">
                            {ticket.messages.length}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View & Reply">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleOpenTicket(ticket); }}
                              sx={{ color: '#0BA480' }}
                            >
                              <Reply fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {filtered.length > 0 && (
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Card>

      {selectedTicket && (
        <TicketDetailDialog
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdated={handleTicketUpdated}
        />
      )}
    </Container>
  );
};

export default AdminSupport;
